import {
  COLOR_NAMES_TO_ROLES_MAP,
  COLOR_ROLE_TO_COLOR_NAME_MAP,
} from '../colors/constants';
import _ from 'lodash';
import type { ColorName } from '../colors';
import type { EditorAPI } from '#packages/editorAPI';

const FONT_MAPPING = {
  font_0: 'Heading1',
  font_2: 'Heading2',
  font_3: 'Heading3',
  font_4: 'Heading4',
  font_5: 'Heading5',
  font_6: 'Heading6',
  font_7: 'Paragraph1',
  font_8: 'Paragraph2',
  font_9: 'Paragraph3',
};

/**
 * Extracts and maps theme colors from the editor API.
 *
 * This function retrieves all colors from the editor API, picks the relevant theme colors,
 * and maps them to their corresponding roles and hex values.
 *
 * @param {EditorAPI} editorAPI - The editor API object that provides access to the theme and colors.
 * @returns {Record<string, { role: string; hex: string }>} An object containing the mapped theme colors with their roles and hex values.
 */
export const extractThemeColors = (editorAPI: EditorAPI) => {
  const colorPalette = editorAPI.theme.colors.getAll();
  const pickedColors = _.pick(
    colorPalette,
    Object.values(COLOR_ROLE_TO_COLOR_NAME_MAP) as ColorName[],
  );

  const result = _.mapValues(pickedColors, (value, key: ColorName) => {
    return {
      role: COLOR_NAMES_TO_ROLES_MAP[key],
      hex: value,
    };
  });

  return result;
};

/**
 * Extracts and maps font themes from the editor API.
 *
 * This function retrieves all text themes from the editor API, maps them to their corresponding roles
 * based on the FONT_MAPPING, and returns an object containing the font themes with their roles.
 *
 * @param {EditorAPI} editorAPI - The editor API object that provides access to the theme and text themes.
 * @returns {Record<string, FontProperties & { role: string }>} An object containing the mapped font themes with their roles.
 */
export const extractThemeFonts = (editorAPI: EditorAPI) => {
  const fontTheme = editorAPI.theme.textThemes.getAll();

  const fonts = _.mapValues(
    fontTheme,
    (fontValue, fontKey: keyof typeof FONT_MAPPING) => {
      if (!FONT_MAPPING[fontKey]) return null; // Skip fonts not in the mapping

      return {
        ...fontValue,
        role: FONT_MAPPING[fontKey],
      };
    },
  );

  return _.pickBy(fonts, (value) => value !== null);
};
